import React, { useState, useEffect } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import QuickAddCustomerModal from '../components/customers/QuickAddCustomerModal';
import api from '../utils/api';
import useFocusRecord from '../utils/useFocusRecord';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import { hasPermission, applyViewScope } from '../utils/permissions';
import { useSortableData } from '../hooks/useSortableData';
import FindDuplicatesButton from '../components/FindDuplicatesButton';

export default function Leads() {
  const { formatAmount, calculateTotal, companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLeadId, setEditingLeadId] = useState(null);  // admin-only full edit
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Multi-filter state — kept lightweight (everything client-side over the
  // already-fetched leads list).
  const [filters, setFilters] = useState({
    status: 'all',         // 'all' | new | contacted | qualified | proposal | hold | negotiation | closed_won | closed_lost | cancelled
    priority: 'all',       // 'all' | low | medium | high | urgent
    salesRepId: 'all',     // 'all' | user.id
    fromDate: '',          // YYYY-MM-DD
    toDate: '',            // YYYY-MM-DD
    activeOnly: false,     // hides closed_won / closed_lost / cancelled
  });
  const resetFilters = () =>
    setFilters({ status: 'all', priority: 'all', salesRepId: 'all', fromDate: '', toDate: '', activeOnly: false });

  // Sync global Fiscal Year selection → list date filter (created_at-based).
  useEffect(() => {
    if (!selectedFy) return;
    setFilters((prev) => ({
      ...prev,
      fromDate: selectedFy.start_date,
      toDate: selectedFy.end_date,
    }));
  }, [selectedFy?.id]);
  const [formData, setFormData] = useState({
    customer_id: '', lead_title: '', description: '', lead_source: 'Website',
    status: 'new', priority: 'medium', estimated_value: '', 
    quote_ref: '', quote_value: '', quote_date: '', expected_close_date: '', notes: '',
    created_by: ''
  });
  const [updateData, setUpdateData] = useState({
    status: '', quote_ref: '', quote_value: '', quote_date: '', notes: '', 
    update_note: '', lost_reason: '', update_date: '', project_value: '',
    won_po_number: '', won_invoice_number: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { 
    fetchLeads();
    fetchCustomers();
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.name : 'Unknown';
  };

  // Resolve a sales-rep name from a lead, trying every available identifier
  // (in order) so we never end up bucketing legitimate reps under "Unknown".
  // Returns null when nothing can be resolved — caller decides whether to
  // skip the row.
  const resolveLeadRepName = (lead) => {
    if (!lead) return null;
    // Try each id field; the FIRST id that maps to a live user wins.
    for (const id of [lead.sales_rep_id, lead.created_by, lead.assigned_to]) {
      if (!id) continue;
      const u = users.find((x) => x.id === id);
      if (u && u.name) return u.name;
    }
    // Then try any snapshot name fields the lead may carry.
    if (lead.sales_rep_name) return lead.sales_rep_name;
    if (lead.created_by_name) return lead.created_by_name;
    return null;
  };

  // Scope-aware base: every aggregate and the table use the same source so
  // the cards never disagree with what the user can actually see.
  // (admins/super_admins keep seeing everything; `own` scope is honoured both
  // server-side and as a defensive client-side filter.)
  const scopedLeads = applyViewScope(leads, user);

  // Calculate overall statistics (base values without tax)
  const totalQuoteValue = scopedLeads.reduce((sum, lead) => sum + (lead.quote_value || 0), 0);
  const totalProjectValue = scopedLeads
    .filter(lead => lead.status === 'closed_won')
    .reduce((sum, lead) => sum + (lead.project_value || 0), 0);

  // Calculate active pipeline value (everything except concluded states)
  const activePipelineValue = scopedLeads
    .filter(lead => !['closed_won', 'closed_lost', 'cancelled'].includes(lead.status))
    .reduce((sum, lead) => sum + (lead.quote_value || lead.estimated_value || 0), 0);

  // Calculate sales rep-wise statistics from the SAME scoped pool. We bucket
  // by the RESOLVED rep name (not by raw id) so different id variants of the
  // same person (legacy sales_rep_id vs created_by) collapse into one row.
  // Leads whose rep cannot be resolved at all are skipped from this table.
  const salesRepStats = {};
  scopedLeads.forEach(lead => {
    const repName = resolveLeadRepName(lead);
    if (!repName) return;  // skip — don't show as "Unknown"
    if (!salesRepStats[repName]) {
      salesRepStats[repName] = {
        name: repName,
        totalLeads: 0,
        totalQuoteValue: 0,
        totalProjectValue: 0
      };
    }
    salesRepStats[repName].totalLeads += 1;
    // Base values without tax
    salesRepStats[repName].totalQuoteValue += (lead.quote_value || 0);
    if (lead.status === 'closed_won') {
      salesRepStats[repName].totalProjectValue += (lead.project_value || 0);
    }
  });

  // Convert to array and sort by total leads
  const salesRepArray = Object.values(salesRepStats).sort((a, b) => b.totalLeads - a.totalLeads);

  // Map raw status codes (closed_won / closed_lost / in_progress …) to a
  // human-readable label so the search query "closed won" matches only
  // closed_won and NOT closed_lost (previous behaviour substring-matched the
  // raw underscored code, causing "closed" to match both).
  const statusLabel = (s) => (s || '')
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase();

  // Enhanced search + multi-filter pipeline over the scoped pool
  const filteredLeads = scopedLeads.filter(lead => {
    // 1) Status filter
    if (filters.status !== 'all' && lead.status !== filters.status) return false;
    // 2) Active-only toggle (excludes terminal states)
    if (filters.activeOnly && ['closed_won', 'closed_lost', 'cancelled'].includes(lead.status)) return false;
    // 3) Priority
    if (filters.priority !== 'all' && (lead.priority || 'medium') !== filters.priority) return false;
    // 4) Sales Rep — match either sales_rep_id (preferred) or created_by
    if (filters.salesRepId !== 'all') {
      const repId = lead.sales_rep_id || lead.created_by;
      if (repId !== filters.salesRepId) return false;
    }
    // 5) Date range on created_at
    if (filters.fromDate || filters.toDate) {
      const created = lead.created_at ? new Date(lead.created_at) : null;
      if (!created) return false;
      if (filters.fromDate && created < new Date(filters.fromDate + 'T00:00:00')) return false;
      if (filters.toDate && created > new Date(filters.toDate + 'T23:59:59')) return false;
    }
    // 6) Free-text search (last so the early-exits above stay fast)
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase().trim();
    const customerName = customers.find(c => c.id === lead.customer_id)?.name?.toLowerCase() || '';
    const salesRepName = getUserName(lead.created_by).toLowerCase();
    const statusText = statusLabel(lead.status);
    return (
      lead.lead_title?.toLowerCase().includes(query) ||
      statusText.includes(query) ||
      lead.quote_ref?.toLowerCase().includes(query) ||
      customerName.includes(query) ||
      salesRepName.includes(query)
    );
  }).sort((a, b) => {
    // Sort by created_at descending (newest first)
    const dateA = new Date(a.created_at || a.updated_at || 0);
    const dateB = new Date(b.created_at || b.updated_at || 0);
    return dateB - dateA;
  });

  const activeFilterCount =
    (filters.status !== 'all' ? 1 : 0) +
    (filters.priority !== 'all' ? 1 : 0) +
    (filters.salesRepId !== 'all' ? 1 : 0) +
    (filters.fromDate ? 1 : 0) +
    (filters.toDate ? 1 : 0) +
    (filters.activeOnly ? 1 : 0);

  // Filtered totals shown above the leads table (the page header cards show
  // dataset-level totals; this strip shows what the user is currently looking at).
  const filteredQuoteValue = filteredLeads.reduce((s, l) => s + (l.quote_value || 0), 0);
  const filteredProjectValue = filteredLeads
    .filter(l => l.status === 'closed_won')
    .reduce((s, l) => s + (l.project_value || 0), 0);

  // Build a roster of sales reps from users + observed lead owners so the
  // dropdown still shows reps even if the user list permissions are tight.
  const repIdsFromLeads = Array.from(new Set(
    scopedLeads.map(l => l.sales_rep_id || l.created_by).filter(Boolean)
  ));
  const salesRepOptions = repIdsFromLeads
    .map(id => ({ id, name: getUserName(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Excel export of the CURRENT filtered+searched view
  const exportLeadsExcel = () => {
    if (filteredLeads.length === 0) {
      alert('No leads to export with the current filters.');
      return;
    }
    const rows = filteredLeads.map((lead) => ({
      'Lead Title': lead.lead_title || '',
      'Customer': customers.find(c => c.id === lead.customer_id)?.name || '',
      'Status': statusLabel(lead.status).replace(/\b\w/g, (c) => c.toUpperCase()),
      'Priority': lead.priority || '',
      'Sales Rep': getUserName(lead.sales_rep_id || lead.created_by),
      'Lead Source': lead.lead_source || '',
      'Quote Ref': lead.quote_ref || '',
      'Quote Value': lead.quote_value || 0,
      'Project Value (Won)': lead.status === 'closed_won' ? (lead.project_value || 0) : '',
      'Won PO #': lead.status === 'closed_won' ? (lead.won_po_number || '') : '',
      'Won Invoice #': lead.status === 'closed_won' ? (lead.won_invoice_number || '') : '',
      'Quote Date': lead.quote_date ? formatDateDMY(lead.quote_date) : '',
      'Expected Close': lead.expected_close_date ? formatDateDMY(lead.expected_close_date) : '',
      'Created At': lead.created_at ? formatDateDMY(lead.created_at) : '',
      'Lost / Cancel Reason': (lead.status === 'closed_lost' || lead.status === 'cancelled') ? (lead.lost_reason || '') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `leads_${stamp}.xlsx`);
  };

  const {
    sortedItems: sortedLeads,
    sortKey: leadSortKey,
    sortDir: leadSortDir,
    requestSort: requestLeadSort,
  } = useSortableData(filteredLeads);
  const leadInd = (k) => leadSortKey !== k ? '↕' : leadSortDir === 'asc' ? '▲' : '▼';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data - remove empty datetime fields
      const submitData = { ...formData };
      if (!submitData.expected_close_date) delete submitData.expected_close_date;
      if (!submitData.quote_date) delete submitData.quote_date;
      if (!submitData.estimated_value) delete submitData.estimated_value;
      if (!submitData.quote_value) delete submitData.quote_value;

      if (editingLeadId) {
        // Admin full edit — PUT to /leads/:id
        await api.put(`/leads/${editingLeadId}`, submitData);
        alert('Lead updated successfully!');
      } else {
        await api.post('/leads', submitData);
        alert('Lead created successfully!');
      }
      fetchLeads();
      setShowForm(false);
      setEditingLeadId(null);
      setFormData({ customer_id: '', lead_title: '', description: '', lead_source: 'Website',
        status: 'new', priority: 'medium', estimated_value: '', 
        quote_ref: '', quote_value: '', quote_date: '', expected_close_date: '', notes: '',
        created_by: '' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving lead');
    }
  };

  // Admin-only: open the full Create form prefilled with the lead's data
  const handleAdminEdit = (lead) => {
    setEditingLeadId(lead.id);
    setFormData({
      customer_id: lead.customer_id || '',
      lead_title: lead.lead_title || '',
      description: lead.description || '',
      lead_source: lead.lead_source || 'Website',
      status: lead.status || 'new',
      priority: lead.priority || 'medium',
      estimated_value: lead.estimated_value || '',
      quote_ref: lead.quote_ref || '',
      quote_value: lead.quote_value || '',
      quote_date: lead.quote_date ? new Date(lead.quote_date).toISOString().split('T')[0] : '',
      expected_close_date: lead.expected_close_date ? new Date(lead.expected_close_date).toISOString().split('T')[0] : '',
      notes: lead.notes || '',
      created_by: lead.created_by || ''
    });
    setShowForm(true);
  };

  // Diagnostics "Open ↗" deep-link: auto-open this lead's edit modal when
  // ?focus=<id> is present. Admins only — handleAdminEdit applies admin
  // edit permissions internally.
  useFocusRecord(leads, handleAdminEdit);

  // Admin-only: hard delete a lead
  const handleAdminDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.lead_title}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      alert('Lead deleted successfully!');
      fetchLeads();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting lead');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Clean up update data - remove empty fields
      const submitData = { ...updateData };
      if (!submitData.quote_date) delete submitData.quote_date;
      if (!submitData.quote_value) delete submitData.quote_value;
      if (!submitData.quote_ref) delete submitData.quote_ref;
      if (!submitData.lost_reason) delete submitData.lost_reason;
      if (!submitData.project_value || submitData.project_value === '') delete submitData.project_value;
      if (!submitData.won_po_number) delete submitData.won_po_number;
      if (!submitData.won_invoice_number) delete submitData.won_invoice_number;

      await api.put(`/leads/${selectedLead.id}`, submitData);

      alert('Lead updated successfully!');
      fetchLeads();
      setShowUpdateModal(false);
      setSelectedLead(null);
    } catch (error) {
      
      // Better error message extraction
      let errorMessage = 'Error updating lead';
      if (error.response) {
        // Server responded with error
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `Error: ${error.response.status} - ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const openUpdateModal = (lead) => {
    setSelectedLead(lead);
    setUpdateData({
      status: lead.status,
      quote_ref: lead.quote_ref || '',
      quote_value: lead.quote_value || '',
      quote_date: lead.quote_date ? new Date(lead.quote_date).toISOString().split('T')[0] : '',
      notes: lead.notes || '',
      update_note: '',
      lost_reason: lead.lost_reason || '',
      update_date: new Date().toISOString().split('T')[0],
      project_value: lead.project_value || '',
      won_po_number: lead.won_po_number || '',
      won_invoice_number: lead.won_invoice_number || ''
    });
    setShowUpdateModal(true);
  };

  const openDetailModal = (lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-purple-100 text-purple-800',
      'qualified': 'bg-cyan-100 text-cyan-800',
      'proposal': 'bg-yellow-100 text-yellow-800',
      'hold': 'bg-gray-100 text-gray-800',
      'negotiation': 'bg-green-100 text-green-800',
      'closed_won': 'bg-green-100 text-green-800',
      'closed_lost': 'bg-red-100 text-red-800',
      'cancelled': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div>Loading...</div></div>;

  return (
    <div className="w-screen max-w-full overflow-x-hidden overflow-y-auto p-2 sm:p-4 lg:p-6">
      <div className="w-full max-w-full overflow-x-hidden">
        {/* Desktop Only: Header with Back Button */}
        <div className="hidden lg:flex lg:items-center lg:justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            🎯 Leads Management
          </h1>
          <button 
            onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Back to Dashboard
          </button>
        </div>
        
        {/* Mobile Only: Add Lead Button */}
        <div className="lg:hidden mb-3">
          {hasPermission(user, 'leads', 'create') && (
            <button onClick={() => { setEditingLeadId(null); setFormData(prev => ({ ...prev, created_by: prev.created_by || user.id })); setShowForm(true); }} className="bg-gradient-to-r from-blue-700 to-green-700 text-white px-3 py-1.5 rounded-lg w-auto text-base font-bold hover:shadow-lg transition-shadow">+ Add Lead</button>
          )}
        </div>
        
        {/* Desktop Only: Add Lead Button */}
        <div className="hidden lg:block mb-4">
          <div className="flex gap-2 flex-wrap">
            {hasPermission(user, 'leads', 'create') && (
              <button onClick={() => { setEditingLeadId(null); setFormData(prev => ({ ...prev, created_by: prev.created_by || user.id })); setShowForm(true); }} className="bg-gradient-to-r from-blue-700 to-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold hover:shadow-lg transition-shadow">+ Add Lead</button>
            )}
            <FindDuplicatesButton
              module="leads"
              labelFields={['client_name', 'project_name', 'status', 'estimated_value', 'currency']}
              onAfterDelete={fetchLeads}
            />
          </div>
        </div>

        {/* Statistics Cards - Mobile Responsive */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 border-l-4 border-blue-500">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase mb-1 sm:mb-2">Total Leads</h3>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600">{leads.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 border-l-4 border-green-600">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase mb-1 sm:mb-2">Total Quote</h3>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">{formatAmount(totalQuoteValue)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 border-l-4 border-amber-500">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase mb-1 sm:mb-2">Pipeline</h3>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-600">{formatAmount(activePipelineValue)}</p>
            <p className="text-xs text-gray-500 mt-1">Excluding closed</p>
          </div>
          <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 border-l-4 border-green-500">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase mb-1 sm:mb-2">Project Value</h3>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">{formatAmount(totalProjectValue)}</p>
            <p className="text-xs text-gray-500 mt-1">Closed won</p>
          </div>
        </div>

        {/* Sales Rep-wise Performance Table — anyone with sales_reports view */}
        {hasPermission(user, 'sales_reports', 'view') && (
          <div className="mobile-table-wrapper bg-white rounded-lg shadow mb-4 sm:mb-6">
            <div className="bg-gradient-to-r from-blue-700 to-green-700 px-4 sm:px-6 py-3 sm:py-4">
              <h2 className="text-lg sm:text-xl font-bold text-white">📊 Sales Rep Performance</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Sales Rep</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Total Leads</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Total Quote Value</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Total Project Value</th>
                  </tr>
                </thead>
                <tbody>
                  {salesRepArray.length > 0 ? (
                    salesRepArray.map((rep) => (
                      <tr key={rep.id || rep.name} className="border-b hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-sky-400 flex items-center justify-center text-white font-bold mr-3">
                              {rep.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-gray-900">{rep.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-bold">
                            {rep.totalLeads}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-semibold text-green-600 text-lg">
                            {formatAmount(rep.totalQuoteValue)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-semibold text-green-600 text-lg">
                            {formatAmount(rep.totalProjectValue)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                        No sales rep data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4 space-y-3" data-testid="leads-filter-bar">
          {/* Row 1: search + active toggle + export */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="text"
              placeholder="🔍 Search by customer, title, status, quote ref, or sales rep..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="leads-search-input"
            />
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm cursor-pointer hover:bg-gray-50 select-none" data-testid="leads-active-only-toggle">
              <input
                type="checkbox"
                checked={filters.activeOnly}
                onChange={(e) => setFilters((f) => ({ ...f, activeOnly: e.target.checked }))}
                className="rounded"
              />
              Active only
            </label>
            <button
              type="button"
              onClick={exportLeadsExcel}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
              data-testid="leads-export-excel-btn"
              title="Export current filtered list to Excel"
            >
              ⬇ Export Excel
            </button>
          </div>

          {/* Row 2: dropdown filters */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="leads-filter-status"
            >
              <option value="all">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="proposal">Proposal</option>
              <option value="hold">On Hold</option>
              <option value="negotiation">Negotiation</option>
              <option value="closed_won">Closed Won</option>
              <option value="closed_lost">Closed Lost</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={filters.priority}
              onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="leads-filter-priority"
            >
              <option value="all">All priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            <select
              value={filters.salesRepId}
              onChange={(e) => setFilters((f) => ({ ...f, salesRepId: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="leads-filter-sales-rep"
            >
              <option value="all">All sales reps</option>
              {salesRepOptions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            <DateInputDMY
              value={filters.fromDate}
              onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="From date"
              data-testid="leads-filter-from-date"
            />
            <DateInputDMY
              value={filters.toDate}
              onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="To date"
              data-testid="leads-filter-to-date"
            />
          </div>

          {/* Row 3: filtered totals + active filter summary + clear button */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t" data-testid="leads-totals-strip">
            <span className="text-xs text-gray-600">
              Showing <b>{filteredLeads.length}</b> of <b>{scopedLeads.length}</b> leads
              {activeFilterCount > 0 && <> · <b>{activeFilterCount}</b> filter{activeFilterCount > 1 ? 's' : ''}</>}
            </span>
            <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200" data-testid="leads-filtered-quote-total">
              Total Quote: <b>{formatAmount(filteredQuoteValue)}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="leads-filtered-project-total">
              Total Project (Won): <b>{formatAmount(filteredProjectValue)}</b>
            </span>
            {(activeFilterCount > 0 || searchQuery) && (
              <button
                type="button"
                onClick={() => { resetFilters(); setSearchQuery(''); }}
                className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline"
                data-testid="leads-clear-filters-btn"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full m-4 my-8">
              <h2 className="text-2xl font-bold mb-4">{editingLeadId ? '🛠 Edit Lead (Admin)' : 'Add New Lead'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm mb-1">Customer *</label>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 min-w-0">
                        <SearchableSelect
                          required
                          value={formData.customer_id}
                          onChange={(val) => setFormData({...formData, customer_id: val})}
                          options={customers.map(c => ({ value: c.id, label: c.name }))}
                          placeholder="Select Customer"
                        />
                      </div>
                      {/* Quick-add customer inline — opens a modal that POSTs
                          /customers and auto-selects the new record. */}
                      <button
                        type="button"
                        onClick={() => setShowQuickAddCustomer(true)}
                        className="shrink-0 px-2.5 py-1.5 rounded border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50"
                        title="Add a new customer without leaving this form"
                        data-testid="lead-form-quick-add-customer"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div><label className="block text-sm mb-1">Lead Title *</label>
                    <input type="text" required value={formData.lead_title} onChange={(e) => setFormData({...formData, lead_title: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., Product Demo Request" />
                  </div>
                  <div><label className="block text-sm mb-1">Lead Source</label>
                    <select value={formData.lead_source} onChange={(e) => setFormData({...formData, lead_source: e.target.value})} className="w-full border rounded px-3 py-2">
                      <option>Website</option><option>Referral</option><option>Cold Call</option><option>Trade Show</option><option>Social Media</option>
                    </select>
                  </div>
                  <div><label className="block text-sm mb-1">Priority</label>
                    <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full border rounded px-3 py-2">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                    </select>
                  </div>
                  <div><label className="block text-sm mb-1">Estimated Value ({companySettings?.currency || 'USD'})</label>
                    <input type="number" value={formData.estimated_value} onChange={(e) => setFormData({...formData, estimated_value: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div><label className="block text-sm mb-1">Expected Close Date</label>
                    <DateInputDMY value={formData.expected_close_date} onChange={(e) => setFormData({...formData, expected_close_date: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm mb-1">
                      Created By
                      {!(user.role === 'admin' || user.role === 'super_admin' || user.role === 'data_entry') && (
                        <span className="text-xs text-gray-400 ml-2">(set by your role — cannot change)</span>
                      )}
                    </label>
                    <SearchableSelect
                      value={formData.created_by || user.id}
                      onChange={(val) => setFormData({...formData, created_by: val})}
                      options={(users || []).map(u => ({ value: u.id, label: `${u.name || u.email}${u.role ? ` (${u.role})` : ''}` }))}
                      placeholder="Select user"
                      disabled={!(user.role === 'admin' || user.role === 'super_admin' || user.role === 'data_entry')}
                      data-testid="lead-created-by-select"
                    />
                    {(user.role === 'admin' || user.role === 'super_admin' || user.role === 'data_entry') && (
                      <p className="text-xs text-amber-600 mt-1">
                        Tip: when entering a lead on behalf of someone else, select the actual sales person here.
                      </p>
                    )}
                  </div>
                </div>
                <div><label className="block text-sm mb-1">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full border rounded px-3 py-2" rows="2" />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setShowForm(false); setEditingLeadId(null); }} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-gradient-to-r from-blue-700 to-green-700 text-white rounded">{editingLeadId ? 'Save Changes' : 'Create Lead'}</button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Detail Modal - Click lead to view */}
        {showDetailModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full m-4 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold">📋 Lead Details: {selectedLead.lead_title}</h2>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>
              
              {/* Lead Information Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-br from-orange-50 to-sky-100 rounded-lg">
                <div className="space-y-3">
                  <div><span className="font-semibold text-gray-700">Customer:</span> <span className="text-gray-900">{customers.find(c => c.id === selectedLead.customer_id)?.name || 'Unknown'}</span></div>
                  <div><span className="font-semibold text-gray-700">Sales Rep:</span> <span className="text-blue-600 font-semibold">👤 {getUserName(selectedLead.created_by)}</span></div>
                  <div><span className="font-semibold text-gray-700">Status:</span> <span className={`ml-2 px-3 py-1 rounded text-sm font-semibold ${getStatusColor(selectedLead.status)}`}>{selectedLead.status.replace('_', ' ').toUpperCase()}</span></div>
                  <div><span className="font-semibold text-gray-700">Priority:</span> <span className={`ml-2 px-2 py-1 rounded text-sm font-semibold ${selectedLead.priority === 'high' ? 'bg-red-100 text-red-800' : selectedLead.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{selectedLead.priority?.toUpperCase()}</span></div>
                  <div><span className="font-semibold text-gray-700">Lead Source:</span> <span className="text-gray-900">{selectedLead.lead_source || '-'}</span></div>
                  <div><span className="font-semibold text-gray-700">Estimated Value:</span> <span className="text-green-600 font-semibold">{selectedLead.estimated_value ? formatAmount(selectedLead.estimated_value) : '-'}</span></div>
                </div>
                <div className="space-y-3">
                  <div><span className="font-semibold text-gray-700">Quote Reference:</span> <span className="text-gray-900 font-mono">{selectedLead.quote_ref || '-'}</span></div>
                  <div><span className="font-semibold text-gray-700">Quote Value:</span> <span className="text-green-600 font-semibold">{selectedLead.quote_value ? formatAmount(selectedLead.quote_value) : '-'}</span></div>
                  <div><span className="font-semibold text-gray-700">Quote Date:</span> <span className="text-gray-900">{selectedLead.quote_date ? formatDateDMY(selectedLead.quote_date) : '-'}</span></div>
                  <div><span className="font-semibold text-gray-700">Expected Close Date:</span> <span className="text-gray-900">{selectedLead.expected_close_date ? formatDateDMY(selectedLead.expected_close_date) : '-'}</span></div>
                  <div><span className="font-semibold text-gray-700">Created:</span> <span className="text-gray-600 text-sm">{new Date(selectedLead.created_at).toLocaleString()}</span></div>
                </div>
              </div>

              {/* Closed Won Details - Show for closed won leads */}
              {selectedLead.status === 'closed_won' && (
                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300">
                  <h3 className="font-bold text-lg text-green-700 mb-3 flex items-center">
                    <span className="mr-2">🎉</span> Deal Closed!
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">💰 Project Value (Base):</span>
                        <span className="text-xl font-bold text-gray-800">
                          {selectedLead.project_value ? formatAmount(parseFloat(selectedLead.project_value)) : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">📊 Tax ({companySettings?.tax_percentage || 0}%):</span>
                        <span className="text-xl font-bold text-amber-600">
                          {selectedLead.project_value ? formatAmount(parseFloat(selectedLead.project_value) * ((companySettings?.tax_percentage || 0) / 100)) : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t-2 border-green-300">
                        <span className="font-bold text-gray-800">💵 Total Amount:</span>
                        <span className="text-2xl font-bold text-green-700">
                          {selectedLead.project_value ? formatAmount(calculateTotal(parseFloat(selectedLead.project_value))) : '-'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">📅 Closed Date:</span>
                      <span className="ml-2 text-lg font-semibold text-green-700 block mt-1">
                        {selectedLead.closed_at ? formatDateDMY(selectedLead.closed_at) : 'Not recorded'}
                      </span>
                      <div className="mt-4 space-y-2">
                        <div>
                          <span className="font-semibold text-gray-700">📄 Customer PO #:</span>
                          <span className="ml-2 font-mono text-gray-800">
                            {selectedLead.won_po_number || <span className="text-gray-400 italic">not provided</span>}
                          </span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">🧾 Invoice #:</span>
                          <span className="ml-2 font-mono text-gray-800">
                            {selectedLead.won_invoice_number || <span className="text-gray-400 italic">not provided</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedLead.description && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">📝 Description:</h3>
                  <p className="text-gray-800">{selectedLead.description}</p>
                </div>
              )}

              {/* Notes */}
              {selectedLead.notes && (
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                  <h3 className="font-semibold text-gray-700 mb-2">📌 Notes:</h3>
                  <p className="text-gray-800">{selectedLead.notes}</p>
                </div>
              )}

              {/* Update History */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <span className="mr-2">🕐</span> Update History 
                  <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {selectedLead.updates_history?.length || 0} update{selectedLead.updates_history?.length !== 1 ? 's' : ''}
                  </span>
                </h3>
                {selectedLead.updates_history && selectedLead.updates_history.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {[...selectedLead.updates_history].reverse().map((update, idx) => (
                      <div key={`upd-${update.updated_at}-${idx}`} className="border-l-4 border-blue-500 pl-4 py-2 bg-gradient-to-r from-blue-50 to-white rounded">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-blue-700">Update #{selectedLead.updates_history.length - idx}</span>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">{new Date(update.updated_at).toLocaleString()}</div>
                            {update.update_date && <div className="text-xs text-blue-600 font-medium">Update Date: {update.update_date}</div>}
                          </div>
                        </div>
                        {update.note && (
                          <div className="mb-2 bg-white p-2 rounded">
                            <p className="text-sm text-gray-700"><strong className="text-blue-600">Note:</strong> {update.note}</p>
                          </div>
                        )}
                        {update.changes && Object.keys(update.changes).length > 0 && (
                          <div className="text-xs text-gray-600 flex flex-wrap gap-2">
                            <strong>Changes:</strong>
                            {update.changes.status && <span className="bg-purple-100 px-2 py-1 rounded">Status → <strong>{update.changes.status}</strong></span>}
                            {update.changes.quote_ref && <span className="bg-green-100 px-2 py-1 rounded">Quote Ref → <strong>{update.changes.quote_ref}</strong></span>}
                            {update.changes.quote_value && <span className="bg-green-100 px-2 py-1 rounded">Quote Value → <strong>{formatAmount(update.changes.quote_value)}</strong></span>}
                            {update.changes.quote_date && <span className="bg-green-100 px-2 py-1 rounded">Quote Date → <strong>{update.changes.quote_date}</strong></span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic p-4 bg-gray-50 rounded">No updates recorded yet</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button onClick={() => setShowDetailModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Close
                </button>
                {selectedLead.status !== 'closed_won' && (
                  <button onClick={() => { setShowDetailModal(false); openUpdateModal(selectedLead); }} className="px-6 py-2 bg-gradient-to-r from-blue-700 to-green-700 text-white rounded-lg hover:from-blue-700 hover:to-green-700 flex items-center">
                    <span className="mr-2">✏️</span> Edit Lead
                  </button>
                )}
                {selectedLead.status === 'closed_won' && (
                  <div className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg flex items-center cursor-not-allowed">
                    <span className="mr-2">🔒</span> Deal Closed - Cannot Edit
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showUpdateModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4 my-8">
              <h2 className="text-2xl font-bold mb-4">Update Lead: {selectedLead.lead_title}</h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm mb-1">Status *</label>
                    <select value={updateData.status} onChange={(e) => setUpdateData({...updateData, status: e.target.value})} className="w-full border rounded px-3 py-2">
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="hold">On Hold</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed_won">Closed Won</option>
                      <option value="closed_lost">Closed Lost</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div><label className="block text-sm mb-1">Quote Reference</label>
                    <input type="text" value={updateData.quote_ref} onChange={(e) => setUpdateData({...updateData, quote_ref: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div><label className="block text-sm mb-1">Quote Value ({companySettings?.currency || 'USD'})</label>
                    <input type="number" value={updateData.quote_value} onChange={(e) => setUpdateData({...updateData, quote_value: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div><label className="block text-sm mb-1">Quote Date</label>
                    <DateInputDMY value={updateData.quote_date} onChange={(e) => setUpdateData({...updateData, quote_date: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div><label className="block text-sm mb-1">Update Date *</label>
                    <DateInputDMY required value={updateData.update_date} onChange={(e) => setUpdateData({...updateData, update_date: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                </div>
                {updateData.status === 'closed_won' && (
                  <div className="bg-green-50 p-4 rounded-lg border-2 border-green-500 space-y-3">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-green-700">💰 Project Value * (Final Won Value)</label>
                      <input
                        type="number"
                        value={updateData.project_value}
                        onChange={(e) => setUpdateData({...updateData, project_value: e.target.value})}
                        className="w-full border rounded px-3 py-2 border-green-500 focus:ring-2 focus:ring-green-500"
                        placeholder="Enter final project value"
                        required={updateData.status === 'closed_won'}
                        data-testid="lead-won-project-value"
                      />
                      <p className="text-xs text-green-600 mt-1">Enter the actual value of the won project</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold mb-1 text-green-700">📄 Customer PO Number</label>
                        <input
                          type="text"
                          value={updateData.won_po_number}
                          onChange={(e) => setUpdateData({...updateData, won_po_number: e.target.value})}
                          className="w-full border rounded px-3 py-2 border-green-500 focus:ring-2 focus:ring-green-500"
                          placeholder="e.g. PO-2026-0123"
                          data-testid="lead-won-po-number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1 text-green-700">🧾 Invoice Number</label>
                        <input
                          type="text"
                          value={updateData.won_invoice_number}
                          onChange={(e) => setUpdateData({...updateData, won_invoice_number: e.target.value})}
                          className="w-full border rounded px-3 py-2 border-green-500 focus:ring-2 focus:ring-green-500"
                          placeholder="e.g. INV-2026-0456"
                          data-testid="lead-won-invoice-number"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-600">
                      Optional but recommended — links this lead to the customer's PO and the sales invoice raised against it.
                    </p>
                  </div>
                )}
                {(updateData.status === 'closed_lost' || updateData.status === 'cancelled') && (
                  <div>
                    <label className="block text-sm mb-1">
                      {updateData.status === 'cancelled' ? 'Cancel Reason *' : 'Lost Reason *'}
                    </label>
                    <textarea
                      required
                      value={updateData.lost_reason}
                      onChange={(e) => setUpdateData({ ...updateData, lost_reason: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      rows="2"
                      placeholder={updateData.status === 'cancelled' ? 'Why was the project cancelled?' : 'Why did we lose this deal?'}
                      data-testid="lead-lost-cancel-reason"
                    />
                  </div>
                )}
                <div><label className="block text-sm mb-1">Update Notes *</label>
                  <textarea required value={updateData.update_note} onChange={(e) => setUpdateData({...updateData, update_note: e.target.value})} className="w-full border rounded px-3 py-2" rows="2" placeholder="What changed?" />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowUpdateModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">💾 Save Update</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          {filteredLeads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">{searchQuery ? 'No leads found' : 'No leads yet'}</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-700 to-green-700 text-white">
                <tr>
                  <th onClick={() => requestLeadSort('customer_id')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Customer <span className="text-[10px] opacity-80">{leadInd('customer_id')}</span></th>
                  <th onClick={() => requestLeadSort('lead_title')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Lead Title <span className="text-[10px] opacity-80">{leadInd('lead_title')}</span></th>
                  <th onClick={() => requestLeadSort('created_by')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Sales Rep <span className="text-[10px] opacity-80">{leadInd('created_by')}</span></th>
                  <th onClick={() => requestLeadSort('status')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Status <span className="text-[10px] opacity-80">{leadInd('status')}</span></th>
                  <th onClick={() => requestLeadSort('quote_ref')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Quote Ref <span className="text-[10px] opacity-80">{leadInd('quote_ref')}</span></th>
                  <th onClick={() => requestLeadSort('quote_value')} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/10">Quote Value <span className="text-[10px] opacity-80">{leadInd('quote_value')}</span></th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedLeads.map((lead) => (
                  <tr key={lead.id} onClick={() => openDetailModal(lead)} className="border-b hover:bg-blue-50 cursor-pointer">
                    <td className="px-4 py-3 font-medium">{customers.find(c => c.id === lead.customer_id)?.name || 'Unknown'}</td>
                    <td className="px-4 py-3">{lead.lead_title}</td>
                    <td className="px-4 py-3 text-blue-600 font-medium">👤 {getUserName(lead.created_by)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(lead.status)}`}>
                        {lead.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{lead.quote_ref || '-'}</td>
                    <td className="px-4 py-3">{lead.quote_value ? formatAmount(lead.quote_value) : '-'}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {lead.status === 'closed_won' ? (
                          <span className="text-gray-400 text-sm font-medium">🔒 Locked</span>
                        ) : hasPermission(user, 'leads', 'edit') ? (
                          <button onClick={() => openUpdateModal(lead)} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-testid={`update-lead-btn-${lead.id}`}>
                            ✏️ Update
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">View Only</span>
                        )}

                        {/* Admin-style full edit & delete — only when user has the rights */}
                        {hasPermission(user, 'leads', 'edit') && (
                          <button
                            onClick={() => handleAdminEdit(lead)}
                            className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                            title="Edit all fields including customer"
                            data-testid={`admin-edit-lead-btn-${lead.id}`}
                          >
                            🛠 Edit
                          </button>
                        )}
                        {hasPermission(user, 'leads', 'delete') && (
                          <button
                            onClick={() => handleAdminDelete(lead)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                            title="Delete this lead"
                            data-testid={`admin-delete-lead-btn-${lead.id}`}
                          >
                            🗑 Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Quick-add customer modal (opened from the Customer field's + button) */}
      {showQuickAddCustomer && (
        <QuickAddCustomerModal
          onClose={() => setShowQuickAddCustomer(false)}
          onCreated={(newCustomer) => {
            // Append + auto-select so the user is back where they started.
            setCustomers((prev) =>
              prev.some((c) => c.id === newCustomer.id) ? prev : [...prev, newCustomer]
            );
            setFormData((prev) => ({ ...prev, customer_id: newCustomer.id }));
          }}
        />
      )}
    </div>
  );
}
